<template>
  <div class="list requirement-list">
    <template v-if="requirementsWithConditions.length == 1">
      <template
        v-for="conditionId in conditionIds[requirementsWithConditions[0].id]"
        :key="conditionId"
        class="requirement-list__condition"
        :class="{ '-no-status': !inSimulation }"
      >
        <span
          class="requirement-list__requirementstatus"
          :class="{
            '-fulfilled': conditionStates[conditionId] === true,
            '-not-fulfilled': conditionStates[conditionId] === false
          }"
        >
          <span v-if="!inSimulation || conditionStates[conditionId] == null" class="pl-1">•</span>
          <lucide-icon
            v-else-if="conditionStates[conditionId] === true"
            name="check"
          />
          <lucide-icon
            v-else-if="conditionStates[conditionId] === false"
            name="x"
          />
        </span>
        <condition-label :condition-id="conditionId" />
      </template>
    </template>

    <template v-else>
      <template
        v-for="(requirement, index) in requirementsWithConditions"
        :key="requirement.id"
      >
        <div
          v-if="index > 0"
          class="requirement-list__separator"
          :style="{ gridRow: `${index * 2 - 1}/${index * 2 + 1}` }"
          :class="{ '-no-line': boxesNeeded }"
        >
          <strong>{{ $t("words.or") }}</strong>
        </div>

        <span
          class="requirement-list__requirementstatus"
          :style="{ gridRow: `${index * 2 + 1}/${index * 2 + 2}` }"
          :class="{
            '-fulfilled': requirementStates[requirement.id] === true,
            '-not-fulfilled': requirementStates[requirement.id] === false
          }"
        >
          <span v-if="!inSimulation || requirementStates[requirement.id] == null" class="pl-1">•</span>
          <lucide-icon
            v-else-if="requirementStates[requirement.id] === true"
            name="check"
          />
          <lucide-icon
            v-else-if="requirementStates[requirement.id] === false"
            name="x"
          />
        </span>

        <condition-label
          v-if="!boxesNeeded"
          :condition-id="conditionIds[requirement.id][0]"
          :style="{ gridRow: `${index * 2 + 1}/${index * 2 + 2}`, gridColumn: '2 / 3' }"
        />

        <div v-else class="requirement-list__requirement" :style="{ gridRow: `${index * 2 + 1}/${index * 2 + 2}` }">
          <h3
            class="requirement-list__requirementtitle"
            v-if="requirement.title"
          >
            {{ requirement.title }}
          </h3>

          <div v-if="!isEmpty(requirement.description)">
            <formatted-text :value="requirement.description" />
          </div>

          <ul
            v-else-if="conditionIds[requirement.id].length > 0"
            class="list gap-2 mb-0 mt-1 pl-0"
          >
            <li
              v-for="conditionId in conditionIds[requirement.id]"
              :key="conditionId"
              class="requirement-list__condition"
              :class="{ '-no-status': conditionIds[requirement.id].length <= 1 }"
            >
              <span
                v-if="conditionIds[requirement.id].length > 1"
                class="requirement-list__conditionstatus"
              >
                <span v-if="!inSimulation || conditionStates[conditionId] == null" class="pl-1">•</span>
                <lucide-icon
                  v-else-if="conditionStates[conditionId] === true"
                  name="check"
                />
                <lucide-icon
                  v-else-if="conditionStates[conditionId] === false"
                  name="x"
                />
              </span>
              <condition-label :condition-id="conditionId" />
            </li>
          </ul>
        </div>
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
/**
 * A non-editable requirements list
 */
import { computed } from "vue";
import { useRoute } from "vue-router";
import { isEmpty } from "@/helpers/utility/Text";

import ConditionLabel from "@editor/items/condition/ConditionLabel.vue";
import FormattedText from "@core/ui/FormattedText.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";

import { useConditionStore } from "@editor/items/condition/conditions.store";
import { useRequirementStore } from "@editor/items/requirement/requirements.store";
import { toUid } from "@/helpers/utility/Id";
import { useSimulationViewStore } from "@editor/views/simulation/simulationView.store";

interface Props {
  itemType: string;
  itemId: number;
}
const props = defineProps<Props>();

const route = useRoute();

const conditionStore = useConditionStore();
const requirementStore = useRequirementStore();
const simulationViewStore = useSimulationViewStore();

const requirements = computed(() => {
  return requirementStore.byItemUid[toUid(props.itemType, props.itemId)] ?? [];
});

const conditionIds = computed(() => {
  const conditions: Record<number, number[]> = {};

  for (const requirement of requirements.value) {
    conditions[requirement.id] = conditionStore.getIdsBySource(requirement);
  }

  return conditions;
});

const requirementsWithConditions = computed(() => {
  return requirements.value.filter(
    (requirement) => conditionIds.value[requirement.id].length > 0
  );
});

const rounds = computed(() => simulationViewStore.rounds);
const inSimulation = computed(
  () =>
    simulationViewStore.simulation != null &&
    ["simulation", "inspector", "evaluation"].includes(route.meta?.areaName as string)
);

const conditionStates = computed(() => {
  if (rounds.value.length == 0) return {};
  const conditionStates: Record<number, boolean> = {};

  for (const requirementId in conditionIds.value) {
    for (const conditionId of conditionIds.value[requirementId]) {
      conditionStates[conditionId] = conditionStore.evaluate(
        conditionId,
        rounds.value
      );
    }
  }

  return conditionStates;
});

const requirementStates = computed(() => {
  if (rounds.value.length == 0) return {};
  const requirementStates: Record<number, boolean> = {};

  for (const requirement of requirements.value) {
    const ids = conditionIds.value[requirement.id];
    const fulfilled = ids.every((conditionId) =>
      conditionStore.evaluate(conditionId, rounds.value)
    );
    requirementStates[requirement.id] = fulfilled;
  }

  return requirementStates;
});

const boxesNeeded = computed(() => {
  return requirementsWithConditions.value.some((requirement) => {
    return conditionIds.value[requirement.id].length > 1 || requirement.title || !isEmpty(requirement.description);
  });
});
</script>

<style lang="scss">
.requirement-list {
  // padding-left: 1.5rem;
  width: max-content;
  max-width: 100%;
  display: grid;
  grid-template-columns: 1.25rem 1fr;
  gap: 0.5rem;
  grid-auto-rows: minmax(1.5rem, auto);

  &.-no-status {
    grid-template-columns: 1fr;
  }

  &__requirement {
    grid-column: 2 / 3;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.75rem;
    border-left: 2px solid var(--color-border-light);
    padding-block: 0;
  }
  &__requirementtitle {
    font-weight: bold;
    color: var(--color-text-medium);
    margin: 0;
    font-size: 1rem;
  }

  &__condition {
    list-style-type: none;
    padding: 0;
    margin: 0;
    display: grid;
    grid-template-columns: 1rem 1fr;
    gap: 0.5rem;

    &.-no-status {
      grid-template-columns: 1fr;
    }
  }

  &__conditionstatus,
  &__requirementstatus {
    color: var(--color-text-light);
    font-size: 14px;
    margin-top: 3px;
    margin-left: -1px;
  }

  &__requirementstatus {
    color: var(--color-text-moderate);
    font-size: 16px;
    grid-column: 1 / 2;

    // &.-fulfilled {
    //   color: var(--color-text-medium);
    // }
    // &.-not-fulfilled {
    //   color: var(--color-text-medium);
    // }
  }

  &__separator {
    pointer-events: none;
    display: flex;
    flex-direction: column;
    justify-content: end;
    grid-column: 1 / 3;
    margin-bottom: -0.5rem;
    width: 3.5rem;
    color: var(--color-text-light);
    line-height: 1em;
    padding-block: 0.5rem;
    padding-bottom: 0.75rem;
    
    text-align: left;
    
    text-transform: uppercase;
    font-size: 12px;

    --separator-color: var(--color-grey);

    // // create a thin horizontal line,
    // // separating the box vertically,
    // // with a gap for the word "or" to the left
    // background-image: linear-gradient(
    //   to bottom,
    //   transparent 0,
    //   transparent 1.5rem,
    //   var(--separator-color) 1.5rem,
    //   var(--separator-color) calc(100% - 1.75rem),
    //   transparent calc(100% - 1.75rem),
    //   transparent calc(100% - 0.5rem),
    //   var(--separator-color) calc(100% - 0.5rem),
    //   var(--separator-color) 100%
    // );
    // background-position: 0.4rem center;
    // background-size: 1px 100%;
    // background-repeat: no-repeat;

    &.-no-line {
      background-image: none;
    }
  }
}
</style>
